import React, { useCallback, useState } from 'react';
import useFetchMyTask from './hooks/useFetchMyTask';
import useFetchCreatedByMe from './hooks/useFetchCreatedByMe';
import MyTaskTable from './table/MyTaskTable';
import AddTaskDialog from './dialog/AddTaskDialog';
import PropTypes from 'prop-types';

import { Tabs, Tab, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import useFetchClassRefRecords from '../MyTime/hooks/useFetchClassRefs';
import useFetchCustomerRefRecords from '../MyTime/hooks/useFetchCustomerRefs';
import useFetchDependencyOptions from './hooks/useFetchDependencyOptions';

const useStyles = makeStyles((theme) => ({
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: '1.4rem',
    margin: 0
  },
  tabsWrapper: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2)
  },
  tab: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.95rem'
  }
}));

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index} id={`task-tabpanel-${index}`} aria-labelledby={`task-tab-${index}`}>
    {value === index && <Box pt={1}>{children}</Box>}
  </div>
);

TabPanel.propTypes = {
  children: PropTypes.any,
  value: PropTypes.any,
  index: PropTypes.any
};

const MyTask = () => {
  const classes = useStyles();

  const [tabIndex, setTabIndex] = useState(0);
  const [addTaskOpen, setAddTaskOpen] = useState(false);

  // Tab 0 – Assigned to Me
  const { myTaskRows, setMyTaskRows, myTaskLoading, refreshMyTasks } = useFetchMyTask();
  const [assignedSelection, setAssignedSelection] = useState([]);
  const [filteredMyTaskCount, setFilteredMyTaskCount] = useState(0);

  // Tab 1 – Created by Me
  const { createdByMeRows, setCreatedByMeRows, createdByMeLoading, refreshCreatedByMe } = useFetchCreatedByMe();
  const [createdSelection, setCreatedSelection] = useState([]);
  const [filteredCreatedByMeCount, setFilteredCreatedByMeCount] = useState(0);

  //fetch class from qbo
  const { classRefs } = useFetchClassRefRecords();
  const { customerRefs } = useFetchCustomerRefRecords();

  const dependencyOptions = useFetchDependencyOptions();

  const handleTabChange = (_, newIndex) => setTabIndex(newIndex);

  const handleAddTask = () => setAddTaskOpen(true);

  const refreshTaskRows = useCallback(
    // eslint-disable-next-line no-undef
    () => Promise.all([refreshMyTasks(), refreshCreatedByMe()]),
    [refreshCreatedByMe, refreshMyTasks]
  );

  return (
    <div className="tw-mt-6">
      {/* Page header */}
      <div className={classes.pageHeader}>
        <h2 className={classes.pageTitle}>My Tasks</h2>
      </div>

      {/* Tabs */}
      <div className={classes.tabsWrapper}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          aria-label="My Tasks tabs"
        >
          <Tab
            id="task-tab-0"
            className={classes.tab}
            label={`Assigned to Me${filteredMyTaskCount ? ` (${filteredMyTaskCount})` : ''}`}
            aria-controls="task-tabpanel-0"
          />
          <Tab
            id="task-tab-1"
            className={classes.tab}
            label={`Created by Me${filteredCreatedByMeCount ? ` (${filteredCreatedByMeCount})` : ''}`}
            aria-controls="task-tabpanel-1"
          />
        </Tabs>
      </div>

      {/* Add Task Dialog */}
      {addTaskOpen && (
        <AddTaskDialog
          open={addTaskOpen}
          setOpen={setAddTaskOpen}
          rows={tabIndex === 0 ? myTaskRows : createdByMeRows}
          setRows={setCreatedByMeRows}
          onTaskSaved={refreshTaskRows}
          classRecords={classRefs}
          customerRecords={customerRefs}
          dependencyOptions={dependencyOptions}
          taskSource="myTask"
        />
      )}

      {/* Tab 0 – Assigned to Me */}
      <TabPanel value={tabIndex} index={0}>
        <MyTaskTable
          tab="assignedToMe"
          rows={myTaskRows}
          onFilteredRowsChange={setFilteredMyTaskCount}
          setRows={setMyTaskRows}
          selection={assignedSelection}
          setSelection={setAssignedSelection}
          loading={myTaskLoading}
          classRecords={classRefs}
          dependencyOptions={dependencyOptions}
          onTaskChanged={refreshTaskRows}
          onAddTask={handleAddTask}
        />
      </TabPanel>

      {/* Tab 1 – Created by Me */}
      <TabPanel value={tabIndex} index={1}>
        <MyTaskTable
          tab="createdByMe"
          rows={createdByMeRows}
          onFilteredRowsChange={setFilteredCreatedByMeCount}
          setRows={setCreatedByMeRows}
          selection={createdSelection}
          setSelection={setCreatedSelection}
          loading={createdByMeLoading}
          classRecords={classRefs}
          dependencyOptions={dependencyOptions}
          onTaskChanged={refreshTaskRows}
          onAddTask={handleAddTask}
        />
      </TabPanel>
    </div>
  );
};

export default MyTask;
